"use client";

import { useEffect, useRef } from "react";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
} from "@/components/tool-kit/styles";
import JeopardyScores from "@/components/tools/games-organizers/jeopardy-scores";
import type { JEOPARDY_GAME_MAKER_STRINGS } from "@/components/tools/jeopardy-game-maker";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import type { Clue } from "@/lib/tool-kit/games-organizers/jeopardy";

type Strings = typeof JEOPARDY_GAME_MAKER_STRINGS;

const DIGIT = /^Digit([1-9])$/;

type KeyAction =
  | { kind: "award"; team: number; sign: 1 | -1 }
  | { kind: "close" }
  | { kind: "reveal" };

/** What a key press means on the clue view, or null to leave it alone. */
function readKey(
  event: KeyboardEvent,
  teams: number,
  canReveal: boolean
): KeyAction | null {
  const target = event.target as HTMLElement | null;
  if (
    event.ctrlKey ||
    event.metaKey ||
    event.altKey ||
    target?.closest("input, textarea, select")
  ) {
    return null;
  }
  const digit = Number(DIGIT.exec(event.code)?.[1] ?? 0);
  if (digit >= 1 && digit <= teams) {
    return { kind: "award", team: digit - 1, sign: event.shiftKey ? -1 : 1 };
  }
  if (event.key === "b" || event.key === "B" || event.key === "Escape") {
    return { kind: "close" };
  }
  const revealKey = event.key === " " || event.key === "Enter";
  // A focused button or link already acts on Space and Enter itself.
  return canReveal && revealKey && !target?.closest("button, a")
    ? { kind: "reveal" }
    : null;
}

/** One clue, full width: the clue, a response reveal, and buttons to score each team. */
export default function JeopardyClue({
  category,
  clue,
  teams,
  scores,
  revealed,
  strings,
  onReveal,
  onAward,
  onClose,
}: {
  category: string;
  clue: Clue;
  teams: readonly string[];
  scores: readonly number[];
  revealed: boolean;
  strings: Strings;
  onReveal: () => void;
  onAward: (team: number, points: number) => void;
  onClose: () => void;
}) {
  const fmt = useToolNumber();
  const reveal = useRef<HTMLButtonElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const hasResponse = clue.response !== "";

  useEffect(() => {
    (revealed || !hasResponse ? close : reveal).current?.focus();
  }, [revealed, hasResponse]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const action = readKey(event, teams.length, hasResponse && !revealed);
      if (!action) {
        return;
      }
      event.preventDefault();
      if (action.kind === "award") {
        onAward(action.team, action.sign * clue.points);
      } else if (action.kind === "close") {
        onClose();
      } else {
        onReveal();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const points = fmt(clue.points);
  return (
    <div className="space-y-6 text-center">
      <p className="font-bold text-muted-foreground text-sm uppercase tracking-wide sm:text-lg">
        {t(strings.clueHeading, { category, points })}
      </p>
      <p className="mx-auto max-w-4xl font-bold text-2xl text-foreground leading-snug sm:text-5xl">
        {clue.clue}
      </p>
      <div aria-live="polite" className="min-h-[4rem]">
        {revealed && hasResponse ? (
          <p className="mx-auto max-w-4xl rounded-lg border border-primary bg-primary/10 p-4 font-semibold text-foreground text-xl sm:text-3xl">
            <span className="sr-only">{strings.responseLabel} </span>
            {clue.response}
          </p>
        ) : null}
        {revealed && !hasResponse ? (
          <p className={HINT}>{strings.noResponse}</p>
        ) : null}
      </div>
      {revealed || !hasResponse ? null : (
        <button
          className={BUTTON_PRIMARY}
          onClick={onReveal}
          ref={reveal}
          type="button"
        >
          {strings.showResponse}
        </button>
      )}
      <div className="space-y-3">
        <h3 className="font-semibold text-foreground">
          {strings.awardHeading}
        </h3>
        <div className="flex flex-wrap justify-center gap-3">
          {teams.map((name, index) => (
            <div
              className="flex items-stretch overflow-hidden rounded-lg border border-border"
              key={`${name}-${index.toString()}`}
            >
              <button
                aria-label={t(strings.takeAria, { points, team: name })}
                className="bg-card px-3 font-bold text-foreground text-lg hover:bg-muted"
                onClick={() => onAward(index, -clue.points)}
                type="button"
              >
                −
              </button>
              <span className="min-w-[6rem] bg-muted/40 px-3 py-2 font-medium text-foreground text-sm">
                {fmt(index + 1)}. {name}
              </span>
              <button
                aria-label={t(strings.addAria, { points, team: name })}
                className="bg-card px-3 font-bold text-foreground text-lg hover:bg-muted"
                onClick={() => onAward(index, clue.points)}
                type="button"
              >
                +
              </button>
            </div>
          ))}
        </div>
        <JeopardyScores scores={scores} strings={strings} teams={teams} />
      </div>
      <div className="space-y-2">
        <button
          className={BUTTON_SECONDARY}
          onClick={onClose}
          ref={close}
          type="button"
        >
          {strings.backToBoard}
        </button>
        <p className={HINT}>{strings.clueKeys}</p>
      </div>
    </div>
  );
}
